import { ErrorModal, dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	Box,
	CircularProgress,
	GlobalStyles,
	Paper,
	Typography,
} from "@mui/material";
import type {
	CellClassParams,
	ColDef,
	ColGroupDef,
	GridApi,
	GridReadyEvent,
	ValueFormatterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import moment, { type Moment } from "moment";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { useCallback, useMemo, useRef, useState } from "react";
import { mesfColors, mesfTheme } from "@/setup/ag-grid/mesf-theme";
import {
	useProductChangesData,
	useProductChangesPickers,
} from "../hooks/useProductChanges";
import {
	type ProductChangeRow,
	STAND_COLUMNS,
	type StandColumn,
} from "../models/ProductChanges";
import { ProductChangesToolbar } from "./ProductChangesToolbar";

/**
 * Twenty-six columns have to fit the screen the way the legacy fits them, so this grid buys
 * its width back from the cell padding. The app installs its theme through
 * `provideGlobalGridOptions`, so a stylesheet override would lose to it — the padding has to
 * come down through theme params. Scoped to this page.
 */
const productChangesTheme = mesfTheme.withParams({
	cellHorizontalPadding: 2,
	headerFontSize: 11,
	fontSize: 12,
});

const CENTER_CELL = { textAlign: "center" } as const;
const CENTER_HEADER = "ag-cell-center-header";
const HEADER_HEIGHT = 28;
const ROW_HEIGHT = 26;
const overlayMessage = (text: string) =>
	`<span style="color:${mesfColors.text.secondary};font-size:13px">${text}</span>`;
const NO_ROWS_OVERLAY = overlayMessage(
	"No product changes in the selected range",
);

// Above this the legacy paints the KPI in bold orangered.
const KPI_ALARM_THRESHOLD = 20;

// The legacy renders a falsy number as an empty cell, so a goal of zero — which is what
// every size pair without a target gets — shows blank rather than "0.00".
const asDecimal = (value: number | null | undefined): string =>
	value ? value.toFixed(2) : "";

const asDateTime = (value: string | null | undefined): string =>
	value ? dxtToLocalServerTime(value, "yyyy-MM-dd HH:mm:ss") : "";

// A production date is a plain date, not an instant: converting it would move it a day.
const asDate = (value: string | null | undefined): string =>
	value ? String(value).slice(0, 10) : "";

const STAND_SET: ReadonlySet<string> = new Set<string>(STAND_COLUMNS);

// The workbook mirrors the grid, column for column.
const EXCEL_FORMATTERS: Record<string, (value: unknown) => string> = {
	ProductionDate: (v) => asDate(v as string | null),
	StartChange: (v) => asDateTime(v as string | null),
	EndChange: (v) => asDateTime(v as string | null),
	ChangeTime: (v) => asDecimal(v as number | null),
	NetChangeTime: (v) => asDecimal(v as number | null),
	Goal: (v) => asDecimal(v as number | null),
	ChangeTimeKPI: (v) => asDecimal(v as number | null),
};

// What each column actually is, in one line. Shown on the header and on every cell as a
// native title, so hovering answers "what is this number" without leaving the grid.
const DESCRIPTIONS: Record<string, string> = {
	ProductionDate: "Production date of the shift the change started in",
	Shift: "Shift that was running when the change started",
	Crew: "Crew working that shift",
	FromProductSize: "Size being rolled before the change",
	ToProductSize: "Size rolled after the change",
	StandChanged: "How many of the 17 stands had their rolls changed",
	StartChange: "Last billet of the outgoing campaign — the change starts here",
	EndChange: "First billet of the incoming campaign — the change ends here",
	ChangeTime: "Minutes between the last billet out and the first billet in",
	NetChangeTime:
		"Change time minus the stops that fell inside it, mechanical ones excepted",
	Goal: "Target minutes for this exact size pair, when one is on record",
	ChangeTimeKPI: "Goal / Total × 100. Over 20 is highlighted",
};

// The legacy paints the whole stands block: the changed ones green, the rest in a second
// colour. Keeping both bands — the point is that the green pops out of a solid strip, and
// without the strip seventeen mostly empty columns read as dead space.
const STAND_CHANGED_BG = `${mesfColors.success.main}66`;
const STAND_UNCHANGED_BG = `${mesfColors.primary.light}4D`;

const standColumn = (stand: StandColumn): ColDef<ProductChangeRow> => ({
	field: stand,
	headerName: stand,
	// Just wide enough for the widest header (HV11) and an x. Every column here is
	// proportional with a floor: the grid fills the width exactly at any size instead of
	// overflowing it. No ceilings — a flex column that hits a maxWidth stops taking part in
	// the share-out, and the grid overflows again on a narrower screen.
	flex: 26,
	minWidth: 26,
	sortable: false,
	headerClass: CENTER_HEADER,
	headerTooltip: `${stand} — marked when this stand's rolls were changed during the incoming campaign`,
	tooltipValueGetter: (p) =>
		p.value
			? `${stand}: rolls changed during the incoming campaign`
			: `${stand}: no roll change on record`,
	valueFormatter: (p: ValueFormatterParams<ProductChangeRow>) =>
		p.value ? "x" : "",
	cellStyle: (p: CellClassParams<ProductChangeRow>) => ({
		...CENTER_CELL,
		backgroundColor: p.value ? STAND_CHANGED_BG : STAND_UNCHANGED_BG,
	}),
});

const RAW_COLUMN_DEFS: (
	| ColDef<ProductChangeRow>
	| ColGroupDef<ProductChangeRow>
)[] = [
	{
		headerName: "Production Data",
		children: [
			{
				field: "ProductionDate",
				headerName: "Date",
				flex: 60,
				minWidth: 62,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
				valueFormatter: (p) => asDate(p.value),
			},
			{
				field: "Shift",
				headerName: "Shift",
				flex: 30,
				minWidth: 30,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
			},
			{
				field: "Crew",
				headerName: "Crew",
				flex: 30,
				minWidth: 30,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
			},
		],
	},
	{
		headerName: "Products",
		children: [
			{
				field: "FromProductSize",
				headerName: "From",
				flex: 82,
				minWidth: 84,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
			},
			{
				field: "ToProductSize",
				headerName: "To",
				flex: 82,
				minWidth: 84,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
			},
		],
	},
	{
		headerName: "Stands",
		children: [
			...STAND_COLUMNS.map(standColumn),
			{
				field: "StandChanged",
				headerName: "Qty",
				flex: 25,
				minWidth: 26,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
			},
		],
	},
	{
		headerName: "Datetime",
		children: [
			{
				field: "StartChange",
				headerName: "Start",
				flex: 110,
				minWidth: 108,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
				valueFormatter: (p) => asDateTime(p.value),
			},
			{
				field: "EndChange",
				headerName: "End",
				flex: 110,
				minWidth: 108,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
				valueFormatter: (p) => asDateTime(p.value),
			},
		],
	},
	{
		headerName: "Duration(min)",
		children: [
			{
				field: "ChangeTime",
				headerName: "Total",
				flex: 40,
				minWidth: 42,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
				valueFormatter: (p) => asDecimal(p.value),
			},
			{
				field: "NetChangeTime",
				headerName: "Actual",
				flex: 40,
				minWidth: 44,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
				valueFormatter: (p) => asDecimal(p.value),
			},
		],
	},
	{
		headerName: "Total",
		children: [
			{
				field: "Goal",
				headerName: "Goal(min)",
				flex: 50,
				minWidth: 54,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
				valueFormatter: (p) => asDecimal(p.value),
			},
			{
				field: "ChangeTimeKPI",
				headerName: "KPI(%)",
				flex: 40,
				minWidth: 44,
				headerClass: CENTER_HEADER,
				valueFormatter: (p) => asDecimal(p.value),
				cellStyle: (p: CellClassParams<ProductChangeRow>) =>
					(p.value ?? 0) > KPI_ALARM_THRESHOLD
						? {
								...CENTER_CELL,
								color: mesfColors.error.main,
								fontWeight: 600,
							}
						: CENTER_CELL,
			},
		],
	},
];

// Walks the groups and hangs each column's description off both the header and the cell.
// Doing it here rather than on every definition keeps the twenty-six columns readable and
// makes it impossible for a column to end up without one.
type AnyDef = ColDef<ProductChangeRow> | ColGroupDef<ProductChangeRow>;

const withTooltips = (defs: AnyDef[]): AnyDef[] =>
	defs.map((def) => {
		if ("children" in def) {
			return { ...def, children: withTooltips(def.children as AnyDef[]) };
		}
		const text = def.field ? DESCRIPTIONS[def.field] : undefined;
		if (!text || def.headerTooltip) return def;
		return { ...def, headerTooltip: text, tooltipValueGetter: () => text };
	});

const COLUMN_DEFS = withTooltips(RAW_COLUMN_DEFS);

// The legacy screen opens on the last three days: today minus two, through today.
const defaultStart = () => moment().startOf("day").subtract(2, "day");
const defaultEnd = () => moment().startOf("day");

const URL_SCHEMA = {
	start: urlParam.date(defaultStart),
	end: urlParam.date(defaultEnd),
	productSizeId: urlParam.int(),
};

export const ProductChangesReport = () => {
	const [
		{ start, end, productSizeId },
		setFilters,
		{ resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<ProductChangeRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const pickersQuery = useProductChangesPickers();
	const dataQuery = useProductChangesData({
		start: startStr,
		end: endStr,
		productSizeId,
	});

	const rows = dataQuery.data ?? [];
	const productSizes = useMemo(
		() => pickersQuery.data ?? [],
		[pickersQuery.data],
	);

	const handleGridReady = useCallback(
		(event: GridReadyEvent<ProductChangeRow>) => {
			gridApiRef.current = event.api;
		},
		[],
	);

	const handleStartChange = useCallback(
		(d: Moment | null) => {
			if (isUsableDate(d)) {
				setFilters({ start: d });
			}
		},
		[setFilters],
	);

	const handleEndChange = useCallback(
		(d: Moment | null) => {
			if (isUsableDate(d)) {
				setFilters({ end: d });
			}
		},
		[setFilters],
	);

	const handleProductSizeChange = useCallback(
		(id: number | null) => {
			setFilters({ productSizeId: id });
		},
		[setFilters],
	);

	// The legacy workbook carries the same text the grid shows — dates as datetimes and
	// the four durations as formatted strings — so the two can be compared side by side.
	const handleExportToExcel = useCallback(() => {
		gridApiRef.current?.exportDataAsExcel({
			fileName: "Product_Changes_Report.xlsx",
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				if (STAND_SET.has(colId)) return params.value ? "x" : "";
				const format = EXCEL_FORMATTERS[colId];
				return format ? format(params.value) : (params.value ?? "");
			},
		});
	}, []);

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}
	if (pickersQuery.error && !errorMsg) {
		setErrorMsg((pickersQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Product Changes Report
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
					".ag-header-group-cell-label": { justifyContent: "center" },
				}}
			/>
			<Paper elevation={3}>
				<ProductChangesToolbar
					onReset={() => resetValues()}
					canReset={!isDefault()}
					start={start}
					end={end}
					onStartChange={handleStartChange}
					onEndChange={handleEndChange}
					productSizeId={productSizeId}
					onProductSizeChange={handleProductSizeChange}
					productSizes={productSizes}
					onExportExcel={handleExportToExcel}
				/>
			</Paper>

			<Paper elevation={3} sx={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
				{dataQuery.isInitialLoading ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
						}}
					>
						<CircularProgress />
					</Box>
				) : (
					<Box sx={{ height: "100%" }}>
						<AgGridReact<ProductChangeRow>
							theme={productChangesTheme}
							columnDefs={COLUMN_DEFS}
							rowData={rows}
							rowModelType="clientSide"
							pagination={false}
							defaultColDef={{ resizable: true, sortable: true }}
							suppressContextMenu
							enableBrowserTooltips
							overlayNoRowsTemplate={NO_ROWS_OVERLAY}
							headerHeight={HEADER_HEIGHT}
							groupHeaderHeight={HEADER_HEIGHT}
							rowHeight={ROW_HEIGHT}
							onGridReady={handleGridReady}
						/>
					</Box>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
